// The Noise Analytics picture of a few seconds of a recording, laid out like Adobe Audition's editor: the
// waveform above, the spectral display below on a linear axis up to half the sample rate, a time ruler, and
// the marks — where the noise is, where its flag goes, flags the reviewer added — with a playhead while it
// plays. Drawn here from the samples, no server work.
import { useEffect, useMemo, useRef, type MouseEvent as ReactMouseEvent } from "react";
import type { Excerpt } from "../lib/wavRange";
import { playbackPosition } from "../lib/wavRange";
import { fft } from "./Spectrogram";

const N = 1024;
const RANGE_DB = 115;          // wide, so quiet high-frequency detail shows as Audition shows it
const WAVE_H = 120;
const SPEC_H = 300;
// Audition's spectral colours: black, violet, crimson, orange, yellow
const STOPS: [number, number, number][] = [[0, 0, 0], [30, 4, 60], [95, 10, 110], [175, 15, 70], [230, 45, 15], [252, 140, 0], [255, 230, 90], [255, 255, 220]];

function color(v: number): [number, number, number] {
  const x = Math.max(0, Math.min(1, v)) * (STOPS.length - 1);
  const i = Math.min(STOPS.length - 2, Math.floor(x));
  const f = x - i;
  const a = STOPS[i], b = STOPS[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

function stft(samples: Float32Array): { db: Float32Array[]; max: number } {
  const hop = Math.max(128, Math.floor(samples.length / 1400));
  const win = new Float64Array(N).map((_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1)));
  const out: Float32Array[] = [];
  let max = -200;
  const re = new Float64Array(N), im = new Float64Array(N);
  for (let s = 0; s + N <= samples.length; s += hop) {
    for (let i = 0; i < N; i++) { re[i] = samples[s + i] * win[i]; im[i] = 0; }
    fft(re, im);
    const col = new Float32Array(N / 2);
    for (let k = 0; k < N / 2; k++) {
      const d = 10 * Math.log10(re[k] * re[k] + im[k] * im[k] + 1e-20);
      col[k] = d;
      if (d > max) max = d;
    }
    out.push(col);
  }
  return { db: out, max };
}

export function clock(t: number, digits = 1) {
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(digits).padStart(digits ? digits + 3 : 2, "0")}`;
}

export interface Mark {
  t: number;
  kind: "noise" | "flag" | "added";
  label?: string;
}

const MARK_STYLE: Record<Mark["kind"], { color: string; dash: number[]; width: number }> = {
  noise: { color: "#39d0ff", dash: [5, 4], width: 2 },
  flag: { color: "#ffd84d", dash: [], width: 2 },
  added: { color: "#ff5fd2", dash: [], width: 2 },
};

export function NoiseView({ excerpt, playId, marks, onPick }: {
  excerpt: Excerpt | null;
  playId: string;                      // the id given to playExcerpt, to draw the playhead
  marks: Mark[];
  /** A click on the picture: seconds on this recording, and whether Shift was held. */
  onPick?: (t: number, shift: boolean) => void;
}) {
  const waveRef = useRef<HTMLCanvasElement>(null);
  const specRef = useRef<HTMLCanvasElement>(null);
  const overRef = useRef<HTMLCanvasElement>(null);
  const spec = useMemo(() => (excerpt && excerpt.samples.length >= N ? stft(excerpt.samples) : null), [excerpt]);
  const span = excerpt ? excerpt.samples.length / excerpt.sampleRate || 1 : 1;

  // the spectral display: one column per frame, rows max-pooled over the bins they cover
  useEffect(() => {
    const cv = specRef.current;
    const g = cv?.getContext("2d");
    if (!cv || !g) return;
    if (!spec || !excerpt) { g.clearRect(0, 0, cv.width, cv.height); return; }
    const W = spec.db.length, H = SPEC_H;
    cv.width = W;
    cv.height = H;
    const img = g.createImageData(W, H);
    const bins = N / 2;
    for (let x = 0; x < W; x++) {
      const col = spec.db[x];
      for (let y = 0; y < H; y++) {
        const kA = Math.floor(((H - y - 1) / H) * bins), kB = Math.max(kA, Math.floor(((H - y) / H) * bins) - 1);
        let m = -200;
        for (let k = kA; k <= kB; k++) if (col[k] > m) m = col[k];
        const [r, gg, b] = color(Math.pow(Math.max(0, (m - (spec.max - RANGE_DB)) / RANGE_DB), 1.25));
        const o = (y * W + x) * 4;
        img.data[o] = r; img.data[o + 1] = gg; img.data[o + 2] = b; img.data[o + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  }, [spec, excerpt]);

  // the waveform: min and max per pixel column
  useEffect(() => {
    const cv = waveRef.current;
    const g = cv?.getContext("2d");
    if (!cv || !g) return;
    const W = 1400, H = WAVE_H;
    cv.width = W;
    cv.height = H;
    g.fillStyle = "#000";
    g.fillRect(0, 0, W, H);
    g.fillStyle = "rgba(30, 217, 143, 0.35)";
    g.fillRect(0, H / 2, W, 1);
    if (!excerpt || !excerpt.samples.length) return;
    const s = excerpt.samples, per = s.length / W;
    g.fillStyle = "#1ed98f";
    for (let x = 0; x < W; x++) {
      let lo = 1, hi = -1;
      for (let i = Math.floor(x * per), e = Math.min(s.length, Math.floor((x + 1) * per)); i < e; i++) {
        if (s[i] < lo) lo = s[i];
        if (s[i] > hi) hi = s[i];
      }
      if (hi < lo) continue;
      const y0 = (1 - hi) * (H / 2), y1 = (1 - lo) * (H / 2);
      g.fillRect(x, y0, 1, Math.max(1, y1 - y0));
    }
  }, [excerpt]);

  // the overlay: ruler, frequency guides, marks, playhead (animated while this excerpt plays)
  useEffect(() => {
    const ov = overRef.current;
    if (!ov || !excerpt) return;
    let raf = 0;
    const draw = () => {
      const g = ov.getContext("2d");
      if (!g) return;
      const W = ov.clientWidth, H = ov.clientHeight;
      if (ov.width !== W) ov.width = W;
      if (ov.height !== H) ov.height = H;
      g.clearRect(0, 0, W, H);
      const x = (t: number) => ((t - excerpt.t0) / span) * W;
      g.font = "10px system-ui, sans-serif";
      // time ruler along the top of the waveform, every half second
      g.textAlign = "left";
      for (let t = Math.ceil(excerpt.t0 * 2) / 2; t <= excerpt.t0 + span; t += 0.5) {
        const px = x(t);
        g.fillStyle = "rgba(255,255,255,0.35)";
        g.fillRect(px, 0, 1, t % 1 === 0 ? 8 : 5);
        if (t % 1 === 0) { g.fillStyle = "rgba(255,255,255,0.7)"; g.fillText(clock(t), px + 3, 10); }
      }
      // frequency guides on the spectral display (linear, as in Audition)
      const nyq = excerpt.sampleRate / 2;
      g.textAlign = "right";
      for (const f of [1000, 5000, 10000, 15000, 20000]) {
        if (f >= nyq) continue;
        const y = WAVE_H + SPEC_H - (f / nyq) * SPEC_H;
        g.fillStyle = "rgba(255,255,255,0.3)";
        g.fillRect(W - 30, y, 30, 1);
        g.fillStyle = "rgba(255,255,255,0.75)";
        g.fillText(`${f / 1000}k`, W - 3, y - 3);
      }
      for (const m of marks) {
        const px = x(m.t);
        if (px < -2 || px > W + 2) continue;
        const st = MARK_STYLE[m.kind];
        g.strokeStyle = st.color;
        g.lineWidth = st.width;
        g.setLineDash(st.dash);
        g.beginPath(); g.moveTo(px, 0); g.lineTo(px, H); g.stroke();
        g.setLineDash([]);
        g.fillStyle = st.color;
        g.beginPath(); g.moveTo(px - 6, 0); g.lineTo(px + 6, 0); g.lineTo(px, 9); g.closePath(); g.fill();
        if (m.label) { g.textAlign = "left"; g.fillText(m.label, px + 5, 22); }
      }
      const pos = playbackPosition(playId);
      if (pos != null) {
        g.strokeStyle = "#ffffff";
        g.lineWidth = 1.5;
        const px = (pos / span) * W;
        g.beginPath(); g.moveTo(px, 0); g.lineTo(px, H); g.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [excerpt, marks, playId, span]);

  function click(e: ReactMouseEvent<HTMLDivElement>) {
    if (!excerpt || !onPick) return;
    const r = e.currentTarget.getBoundingClientRect();
    onPick(excerpt.t0 + Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * span, e.shiftKey);
  }

  return (
    <div className={`nv${onPick ? " pickable" : ""}`} onClick={click}
         onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}>
      <canvas ref={waveRef} className="nv-wave" style={{ height: WAVE_H }} />
      <canvas ref={specRef} className="nv-spec" style={{ height: SPEC_H }} />
      <canvas ref={overRef} className="nv-over" />
      {!excerpt && <div className="nv-wait"><span className="spinner" style={{ width: 16, height: 16 }} /> Reading the recording…</div>}
    </div>
  );
}
